// /health/inbody/import — загрузка PDF-отчёта DDX и сверка перед сохранением.
// Режимы — по адресу: без параметров — выбор файла; ?shared=1 — файл из «Поделиться» (лежит во
// «входящих» SW, src/pages/inbody/sharedInbox.ts); ?share_failed=1 — подсказка выбрать файл
// вручную; ?draft=<id> — сверка черновика; ?manual=1 — ручной ввод без PDF.
// Файл из «Поделиться» убирается из «входящих» только после ответа сервера: при блокировке (423)
// и входе (401) экран откроется снова и загрузит его сам; без сети — повтор по событию `online`.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { Button, Card, ErrorText, Icon, Input, PageSpinner, Spinner, useToast } from '@/components/ui'
import { formatDate } from '@/lib/format'
import { todayIso } from '@/lib/periods'

import { inbodyApi, inbodyKeys, useDraft, useInvalidateInBody, type DraftOut, type DraftUploadOut, type UploadOrigin } from './api'
import { toForm, type ScanPayload } from './formState'
import { ScanForm } from './ScanForm'
import { clearSharedFile, peekSharedFile } from './sharedInbox'

const IMPORT_PATH = '/health/inbody/import'
export const OFFLINE_TEXT = 'Нет сети — разберу, когда появится связь'
export const SHARE_FAILED_TEXT = 'Не удалось принять файл — выбери его вручную'

type UploadState =
  | { kind: 'idle' }
  | { kind: 'uploading'; name: string }
  | { kind: 'offline'; name: string }
  | { kind: 'failed'; error: unknown }

/** Загрузка PDF: черновик или отказ; без сети — ждёт связь и повторяет сама. */
function useUpload(onDone: (result: DraftUploadOut) => void) {
  const [state, setState] = useState<UploadState>({ kind: 'idle' })
  const pendingRef = useRef<{ file: File; origin: UploadOrigin; fromInbox: boolean } | null>(null)
  const doneRef = useRef(onDone)
  useEffect(() => {
    doneRef.current = onDone
  })

  const run = async (file: File, origin: UploadOrigin, fromInbox: boolean) => {
    pendingRef.current = { file, origin, fromInbox }
    setState({ kind: 'uploading', name: file.name })
    try {
      const result = await inbodyApi.uploadDraft(file, origin)
      pendingRef.current = null
      if (fromInbox) await clearSharedFile()
      setState({ kind: 'idle' })
      doneRef.current(result)
    } catch (error) {
      if (error instanceof ApiError && error.isNetwork) {
        setState({ kind: 'offline', name: file.name })
        return
      }
      pendingRef.current = null
      // 401 и 423 — вход и разблокировка: файл из «Поделиться» остаётся ждать во «входящих».
      if (fromInbox && !(error instanceof ApiError && (error.status === 401 || error.status === 423))) {
        await clearSharedFile()
      }
      setState({ kind: 'failed', error })
    }
  }

  useEffect(() => {
    if (state.kind !== 'offline') return
    const retry = () => {
      const pending = pendingRef.current
      if (pending) void run(pending.file, pending.origin, pending.fromInbox)
    }
    window.addEventListener('online', retry)
    return () => window.removeEventListener('online', retry)
  }, [state.kind])

  return { state, run, reset: () => setState({ kind: 'idle' }) }
}

function parseFailed(error: unknown): boolean {
  return error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 401 && error.status !== 423
}

function Back() {
  return (
    <Link to="/health/inbody" className="-ml-1 mb-2 inline-flex min-h-11 items-center gap-1 text-sm text-muted hover:text-fg lg:min-h-8">
      <Icon name="chevron-left" size={18} />
      Состав тела
    </Link>
  )
}

function Title({ children }: { children: ReactNode }) {
  return <h2 className="mb-3 text-lg font-semibold text-fg">{children}</h2>
}

function UploadStep({ hint, shared }: { hint?: string; shared: boolean }) {
  const navigate = useNavigate()
  const toast = useToast()
  const client = useQueryClient()
  const startedRef = useRef(false)
  const [inboxEmpty, setInboxEmpty] = useState(false)

  const upload = useUpload(({ draft, duplicate }) => {
    client.setQueryData(inbodyKeys.draft(draft.id), draft)
    void client.invalidateQueries({ queryKey: inbodyKeys.drafts })
    if (duplicate) toast.show({ message: 'Этот отчёт уже загружен' })
    if (duplicate && draft.saved_scan_id) navigate(`/health/inbody/${draft.saved_scan_id}`, { replace: true })
    else navigate(`${IMPORT_PATH}?draft=${draft.id}`, { replace: true })
  })

  // Файл из «Поделиться»: забрать из «входящих» и загрузить самим.
  useEffect(() => {
    if (!shared || startedRef.current) return
    startedRef.current = true
    void (async () => {
      const file = await peekSharedFile()
      if (file) void upload.run(file, 'share', true)
      else setInboxEmpty(true)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shared])

  const { state } = upload
  const failed = state.kind === 'failed' ? state.error : null
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            PDF-отчёт InBody из приложения DDX. Цифры разберутся сами — перед сохранением их можно проверить и
            поправить. Повторная загрузка того же файла второго черновика не создаёт.
          </p>
          {hint || inboxEmpty ? (
            <p className="flex items-start gap-2 text-sm text-warning" role="status">
              <Icon name="info" size={18} className="mt-px shrink-0" />
              {hint ?? SHARE_FAILED_TEXT}
            </p>
          ) : null}
          {state.kind === 'uploading' ? (
            <p className="flex items-center gap-2 text-sm text-muted" role="status">
              <Spinner size={18} /> Разбираю «{state.name}»…
            </p>
          ) : null}
          {state.kind === 'offline' ? (
            <p className="flex items-start gap-2 text-sm text-warning" role="status">
              <Icon name="wifi-off" size={18} className="mt-px shrink-0" />
              {OFFLINE_TEXT}
            </p>
          ) : null}
          <Input
            label="PDF-отчёт"
            type="file"
            accept="application/pdf,.pdf"
            disabled={state.kind === 'uploading'}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void upload.run(file, 'upload', false)
              e.target.value = ''
            }}
          />
          {failed ? (
            <div className="flex flex-col gap-3">
              <ErrorText error={failed} />
              {parseFailed(failed) ? (
                <div>
                  <Button onClick={() => navigate(`${IMPORT_PATH}?manual=1`)}>Ввести вручную</Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>
      <p className="text-sm text-muted">
        Отчёта нет под рукой? <Link to={`${IMPORT_PATH}?manual=1`} className="text-accent underline-offset-2 hover:underline">Введи цифры вручную</Link>.
      </p>
    </div>
  )
}

function useSaveScan(draftId: string | null) {
  const navigate = useNavigate()
  const toast = useToast()
  const invalidate = useInvalidateInBody()
  return useMutation({
    mutationFn: (payload: ScanPayload) => inbodyApi.saveScan({ ...payload, draft_id: draftId }),
    networkMode: 'always',
    onSuccess: (scan) => {
      void invalidate()
      toast.show({ message: 'Замер сохранён', tone: 'accent' })
      navigate(`/health/inbody/${scan.id}`, { replace: true })
    },
  })
}

function DraftReview({ draft }: { draft: DraftOut }) {
  const navigate = useNavigate()
  const toast = useToast()
  const invalidate = useInvalidateInBody()
  const save = useSaveScan(draft.id)
  const discard = useMutation({
    mutationFn: () => inbodyApi.discardDraft(draft.id),
    networkMode: 'always',
    onSuccess: () => {
      void invalidate()
      toast.show({ message: 'Черновик удалён' })
      navigate('/health/inbody', { replace: true })
    },
    onError: (error) => toast.show({ message: error instanceof ApiError ? error.message : 'Не удалось удалить', tone: 'danger' }),
  })
  const values = draft.parsed.values
  const notice = draft.existing_scan_id ? (
    <p className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-sm text-warning" role="status">
      <Icon name="alert" size={18} className="mt-px shrink-0" />
      <span>
        За {formatDate(values.day)} уже есть замер — сохранение заменит его.{' '}
        <Link to={`/health/inbody/${draft.existing_scan_id}`} className="underline">
          Открыть
        </Link>
      </span>
    </p>
  ) : null
  return (
    <>
      <Title>Проверь цифры отчёта{draft.filename ? ` «${draft.filename}»` : ''}</Title>
      <ScanForm
        initial={toForm(values, { measured_at: draft.suggested_measured_at })}
        warnings={draft.parsed.warnings}
        notice={notice}
        historyCount={draft.history_new_days.length}
        pending={save.isPending}
        error={save.error}
        onSubmit={(payload) => save.mutate(payload)}
        onCancel={() => navigate('/health/inbody')}
        extraActions={
          <Button variant="ghost" onClick={() => discard.mutate()} loading={discard.isPending}>
            Удалить черновик
          </Button>
        }
      />
    </>
  )
}

function DraftStep({ id }: { id: string }) {
  const draft = useDraft(id)
  if (draft.isPending) return <PageSpinner />
  if (draft.isError) {
    const notFound = draft.error instanceof ApiError && draft.error.status === 404
    return (
      <>
        <ErrorText error={notFound ? null : draft.error}>
          {notFound ? 'Черновик не найден — возможно, он уже удалён. Загрузи отчёт заново.' : undefined}
        </ErrorText>
        <div className="mt-4">
          <UploadStep shared={false} />
        </div>
      </>
    )
  }
  if (draft.data.saved_scan_id) return <Navigate to={`/health/inbody/${draft.data.saved_scan_id}`} replace />
  return <DraftReview draft={draft.data} />
}

function ManualStep() {
  const navigate = useNavigate()
  const save = useSaveScan(null)
  const [day] = useState(todayIso)
  // Подсказанное время на сегодня (R26); не ответило — время оставим серверу.
  const suggested = useQuery({
    queryKey: [...inbodyKeys.all, 'suggest-time', day],
    queryFn: () => inbodyApi.suggestTime(day, null),
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })
  if (suggested.isPending) return <PageSpinner />
  return (
    <>
      <Title>Ввести замер вручную</Title>
      <p className="mb-4 text-sm text-muted">Обязательны дата и вес, остальное — по желанию. Такой замер сохранится без PDF.</p>
      <ScanForm
        initial={toForm({ day }, { measured_at: suggested.data?.measured_at ?? null })}
        pending={save.isPending}
        error={save.error}
        onSubmit={(payload) => save.mutate(payload)}
        onCancel={() => navigate('/health/inbody')}
      />
    </>
  )
}

export function ImportPage() {
  const [params] = useSearchParams()
  const draftId = params.get('draft')
  let body: ReactNode
  if (draftId) body = <DraftStep key={draftId} id={draftId} />
  else if (params.get('manual') === '1') body = <ManualStep />
  else
    body = (
      <>
        <Title>Загрузить отчёт InBody</Title>
        <UploadStep shared={params.get('shared') === '1'} hint={params.get('share_failed') === '1' ? SHARE_FAILED_TEXT : undefined} />
      </>
    )
  return (
    <div className="flex flex-col">
      <Back />
      {body}
    </div>
  )
}
